import {Component, inject, input, OnInit} from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Contrato, ContratoCliente, ContratoFiltros} from '../clientes.interface';
import {ClientesService} from '../clientes.service';
import {ErrorService} from '../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {Badge} from '../../../../../../shared/components/ui/badge/badge';
import {DatePipe} from '@angular/common';
import {GenericHttpResponse} from '../../../../../../shared/services/interfaces/generic-http-response.interface';
import {HttpErrorResponse} from '@angular/common/http';
import {DialogService} from '@shared/services/dialog.service';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {PageHeaderComponent} from '../../../../../../shared/components/common/page-header/page-header.component';
import {DeleteTrigger} from '../../../../../../shared/components/ui/delete-trigger/delete-trigger';
import {CrearContrato} from './crear-contrato/crear-contrato';
import {
  Pagination,
  SearchInput,
  FilterSelect,
  FilterDate,
  FilterOption,
  DataTable,
  TableColumn,
  TableCellDirective,
  FiltersContainer
} from '../../../../../../shared/components/ui/list-components';

@Component({
  selector: 'app-contratos-cliente',
  imports: [
    HasPermissionDirective,
    Badge,
    DatePipe,
    MatIcon,
    MatTooltip,
    PageHeaderComponent,
    DeleteTrigger,
    Pagination,
    SearchInput,
    FilterSelect,
    FilterDate,
    DataTable,
    TableCellDirective,
    FiltersContainer,
  ],
  templateUrl: './contratos-cliente.html',
})
export class ContratosCliente implements OnInit {
  readonly permissionAuth = inject(AuthenticationService);

  clienteId = input.required<number>();

  loading = false;
  deleting = false;
  deletingIndex: number | null = null;
  downloadingDocumentId: number | null = null;
  contratos: Contrato[] = [];
  contratoActivo: ContratoCliente | null = null;

  // Paginación
  currentPage = 1;
  lastPage = 1;
  perPage = 15;
  total = 0;

  // Filtros
  filtros: ContratoFiltros = {
    busqueda: '',
    activo: '',
    fecha_desde: '',
    fecha_hasta: ''
  };

  // Columnas de la tabla
  columns: TableColumn[] = [
    { key: 'nro_contrato', header: 'Número' },
    { key: 'fecha_inicio', header: 'Fecha Inicio' },
    { key: 'fecha_fin', header: 'Fecha Fin' },
    { key: 'establecimiento', header: 'Establecimiento' },
    { key: 'estado', header: 'Estado' },
    { key: 'acciones', header: 'Acciones' }
  ];

  // Opciones de filtros
  estadoOptions: FilterOption[] = [
    { value: 'true', label: 'Activo' },
    { value: 'false', label: 'Cerrado' }
  ];

  private clientesService = inject(ClientesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogService = inject(DialogService);

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.getContratos();
    this.getContratoActivo();
  }

  getContratos(page: number = 1): void {
    this.loading = true;
    this.clientesService.getContratos(this.clienteId(), page, this.perPage, this.filtros).subscribe({
      next: (response: GenericHttpResponse<any>) => {
        if (response.status && response.data) {
          this.contratos = response.data.data;
          this.currentPage = response.data.pagination.current_page;
          this.lastPage = response.data.pagination.last_page;
          this.total = response.data.pagination.total;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  getContratoActivo(): void {
    this.clientesService.getContratoActivo(this.clienteId()).subscribe({
      next: (response: GenericHttpResponse<ContratoCliente>) => {
        this.contratoActivo = response.status && response.data ? response.data : null;
      },
      error: (error: HttpErrorResponse) => {
        this.contratoActivo = null;
        this.errorService.handleError(error);
      }
    });
  }

  // Handlers de filtros
  onSearchChange(value: string): void {
    this.filtros.busqueda = value;
    this.currentPage = 1;
    this.getContratos();
  }

  onEstadoChange(value: string | number): void {
    this.filtros.activo = value as string;
    this.currentPage = 1;
    this.getContratos();
  }

  onFechaDesdeChange(value: string): void {
    this.filtros.fecha_desde = value;
    this.currentPage = 1;
    this.getContratos();
  }

  onFechaHastaChange(value: string): void {
    this.filtros.fecha_hasta = value;
    this.currentPage = 1;
    this.getContratos();
  }

  get hasActiveFilters(): boolean {
    return Object.values(this.filtros).some(valor => valor !== null && valor !== undefined && valor !== '');
  }

  clearFilters(): void {
    this.filtros = {
      busqueda: '',
      activo: '',
      fecha_desde: '',
      fecha_hasta: ''
    };
    this.currentPage = 1;
    this.getContratos();
  }

  // Paginación
  onPageChange(page: number): void {
    this.getContratos(page);
  }

  onPerPageChange(perPage: number): void {
    this.perPage = perPage;
    this.currentPage = 1;
    this.getContratos(1);
  }

  onRowClick(event: { item: Contrato; index: number }): void {
    this.openEditModal(event.item);
  }

  openCreateModal(): void {
    this.dialogService.openForm(CrearContrato, {
      size: 'md',
      data: {
        clienteId: this.clienteId(),
        contrato: null
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  openEditModal(contrato: Contrato): void {
    this.dialogService.openForm(CrearContrato, {
      size: 'md',
      data: {
        clienteId: this.clienteId(),
        contrato: contrato
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadData();
      }
    });
  }

  toggleActivo(contrato: Contrato, event: Event): void {
    event.stopPropagation(); // Prevent row click
    const nuevoEstado = !contrato.activo;
    this.clientesService.updateContrato(contrato.id, { activo: nuevoEstado }).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar(
            nuevoEstado ? 'Contrato activado correctamente' : 'Contrato cerrado correctamente'
          );
          this.loadData();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      }
    });
  }

  descargarDocumento(contrato: Contrato, event: Event): void {
    event.stopPropagation();

    if (!contrato.documento) {
      return;
    }

    this.downloadingDocumentId = contrato.id;
    this.clientesService.descargarDocumentoContrato(
      contrato.id,
      contrato.documento.nombre || `contrato-${contrato.id}`
    ).subscribe({
      next: () => this.snackbarService.showSnackBar('Documento descargado correctamente'),
      error: (error: HttpErrorResponse) => this.errorService.handleError(error),
    }).add(() => {
      this.downloadingDocumentId = null;
    });
  }

  delete(id: number, index: number): void {
    this.deleting = true;
    this.deletingIndex = index;

    this.clientesService.destroyContrato(id).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Contrato eliminado correctamente');
          this.loadData();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.deleting = false;
      this.deletingIndex = null;
    });
  }
}
